import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FiShield } from "react-icons/fi";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import bcrypt from "bcryptjs";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();

  const handleLogin = async () => {
    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const adminDocRef = doc(db, "admins", email);
      const adminSnapshot = await getDoc(adminDocRef);

      if (!adminSnapshot.exists()) {
        throw new Error("Invalid email or password");
      }

      const adminData = adminSnapshot.data();

      if (!adminData.passwordHash) {
        throw new Error("Admin account is misconfigured");
      }

      const passwordMatch = await bcrypt.compare(password, adminData.passwordHash);
      if (!passwordMatch) {
        throw new Error("Invalid email or password");
      }

      localStorage.setItem(
        "adminInfo",
        JSON.stringify({
          email: adminData.email,
          name: adminData.displayName || adminData.email,
          role: adminData.role || "admin",
        })
      );

      navigate("/admin", { replace: true });
    } catch (err) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleLogin();
  };

  return (
    <div className="min-h-screen bg-[#01030b] text-white flex items-center justify-center px-5">
      <div className="relative w-full max-w-md">
        <div className="bg-[#0b0f1a] rounded-3xl border border-gray-800 p-8 sm:p-10">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center">
              <FiShield className="text-cyan-400 w-8 h-8" />
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl font-extrabold text-center mb-3 text-cyan-400">
            Matches Guru Admin
          </h1>
          <p className="text-center text-gray-400 text-sm mb-8">
            Secure portal for management
          </p>

          {error && (
            <div className="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-xl mb-6 text-center text-sm">
              {error}
            </div>
          )}

          <div className="space-y-5">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Email</label>
              <input
                type="email"
                placeholder="admin@example.com"
                className="w-full px-5 py-3.5 bg-[#050814]/70 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 transition"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Password</label>
              <input
                type="password"
                placeholder="••••••••"
                className="w-full px-5 py-3.5 bg-[#050814]/70 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 transition"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>

            <button
              onClick={handleLogin}
              disabled={loading}
              className={`w-full mt-4 py-4 rounded-xl font-bold text-lg transition ${
                loading
                  ? "bg-cyan-700/50 cursor-not-allowed"
                  : "bg-cyan-500 hover:bg-cyan-400"
              }`}
            >
              {loading ? "Authenticating..." : "Secure Login"}
            </button>
          </div>

          <p className="text-center text-xs text-gray-500 mt-8">
            Matches Guru • Authorized personnel only
          </p>
        </div>

        <div className="text-center mt-6">
          <Link
            to="/"
            className="text-cyan-400 hover:text-cyan-300 text-sm transition"
          >
            ← Return to main site
          </Link>
        </div>
      </div>
    </div>
  );
}
